import React, { useState } from 'react';

interface ScreenshotFrameProps {
  src: string;
  alt: string;
  /**
   * Fake URL to show in the browser bar, e.g. "app.sprout.education/observations".
   * When omitted, the URL pill is hidden.
   */
  url?: string;
  className?: string;
  /**
   * Rendered when the image fails to load (missing file, network error, etc.).
   * Typically a `<MockupPreview type="..." />` so the site never shows a broken
   * image on the marketing pages.
   */
  fallback?: React.ReactNode;
}

/**
 * Wraps a real app screenshot in browser-style chrome (three traffic-light
 * dots + URL pill), with shadow and rounded corners. Works in both light and
 * dark mode, lazy-loads the image, and falls back gracefully to the supplied
 * `fallback` node if the image can't be loaded.
 */
const ScreenshotFrame: React.FC<ScreenshotFrameProps> = ({
  src,
  alt,
  url,
  className = '',
  fallback,
}) => {
  const [failed, setFailed] = useState(false);

  if (failed && fallback) {
    return <>{fallback}</>;
  }

  return (
    <div
      data-testid="screenshot-frame"
      className={`relative rounded-2xl overflow-hidden shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 ${className}`}
    >
      {/* Browser chrome */}
      <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2.5 flex items-center space-x-2 border-b border-gray-200 dark:border-gray-700">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" aria-hidden="true" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" aria-hidden="true" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" aria-hidden="true" />
        {url && (
          <div className="flex-1 mx-3">
            <div className="bg-white dark:bg-gray-900 rounded-md px-3 py-1 text-[10px] text-gray-400 dark:text-gray-500 text-center truncate font-mono">
              {url}
            </div>
          </div>
        )}
      </div>

      {/* Image */}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setFailed(true)}
        className="w-full h-auto block"
      />
    </div>
  );
};

export default ScreenshotFrame;
